import React, { useState, useEffect } from 'react';
import {
  changePhone,
  changeBornDate,
  changeLanguage
} from '../../api/userService';

import { languageFlags, supportedLanguages } from '../../constants/languageFlags';

interface UserData {
  email: string;
  created_at: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  born_date?: string;
  language_code?: string;
}

interface SettingsPanelProps {
  userData: UserData | null;
  t: Function;
  refreshUserData: () => Promise<void>;
  setShowNotification: React.Dispatch<React.SetStateAction<boolean>>;
  setNotificationMessage: React.Dispatch<React.SetStateAction<string>>;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({
  userData,
  t,
  refreshUserData,
  setShowNotification,
  setNotificationMessage
}) => {
  const [newPhone, setNewPhone] = useState('');
  const [newBornDate, setNewBornDate] = useState('');
  const [newLanguage, setNewLanguage] = useState('es');

  useEffect(() => {
    if (userData) {
      setNewPhone(userData.phone_number || '');
      setNewBornDate(userData.born_date?.split('T')[0] || '');
      setNewLanguage(userData.language_code || 'es');
    }
  }, [userData]);

  const handleSavePhone = async () => {
    try {
      await changePhone(newPhone);
      await refreshUserData();
      setNotificationMessage(t('settings.notifications.dataUpdated'));
      setShowNotification(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveBornDate = async () => {
    try {
      await changeBornDate(newBornDate);
      await refreshUserData();
      setNotificationMessage(t('settings.notifications.dataUpdated'));
      setShowNotification(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveLanguage = async () => {
    try {
      await changeLanguage(newLanguage);
      await refreshUserData();
      setNotificationMessage(t('settings.notifications.languageUpdated'));
      setShowNotification(true);
    } catch (err) {
      console.error(err);
    }
  };

  if (!userData) return null;

  return (
    <div className="bg-[#0f172a] text-white rounded-lg shadow-md p-6 w-full md:w-1/2">
      <h3 className="text-lg font-bold mb-4">{t('settings.myAccountTitle')}</h3>

      {/* Teléfono */}
      <div className="mb-4">
        <label className="flex flex-col text-sm">
          {t('settings.labels.phoneNumber')}:
          <input
            type="text"
            className="
              bg-gray-700 text-white
              border border-gray-600
              p-2 rounded mt-1
            "
            value={newPhone}
            onChange={(e) => setNewPhone(e.target.value)}
          />
        </label>
        <button
          onClick={handleSavePhone}
          className="
            bg-gray-800 
            text-white 
            border border-gray-600 
            rounded 
            px-4 py-2 
            mt-2
            hover:bg-gray-700
          "
        >
          {t('settings.buttons.savePhone')}
        </button>
      </div>

      {/* Fecha de nacimiento */}
      <div className="mb-4">
        <label className="flex flex-col text-sm">
          {t('settings.labels.bornDate')}:
          <input
            type="date"
            className="
              bg-gray-700 text-white
              border border-gray-600
              p-2 rounded mt-1
            "
            value={newBornDate}
            onChange={(e) => setNewBornDate(e.target.value)}
          />
        </label>
        <button
          onClick={handleSaveBornDate}
          className="
            bg-gray-800 
            text-white 
            border border-gray-600
            rounded 
            px-4 py-2 
            mt-2
            hover:bg-gray-700
          "
        >
          {t('settings.buttons.saveBornDate')}
        </button>
      </div>

      {/* Idioma */}
      <div>
        <label className="flex flex-col text-sm">
          {t('settings.labels.languageCode')}:
          <div className="flex items-center gap-2 mt-1">
            <select
              className="
                bg-gray-700 text-white 
                border border-gray-600 
                p-2 
                rounded 
                text-sm
              "
              value={newLanguage}
              onChange={(e) => setNewLanguage(e.target.value)}
            >
              {supportedLanguages.map(({ code, nameKey }) => (
                <option key={code} value={code}>
                  {t(`settings.language.${nameKey}`)}
                </option>
              ))}
            </select>

            {languageFlags[newLanguage] && (
              <img
                src={languageFlags[newLanguage]}
                alt={`${newLanguage} flag`}
                className="w-6 h-6 rounded"
              />
            )}
          </div>
        </label>
        <button
          onClick={handleSaveLanguage}
          className="
            bg-gray-800 
            text-white 
            border border-gray-600 
            rounded 
            px-4 py-2 
            mt-2
            hover:bg-gray-700
          "
        >
          {t('settings.buttons.saveLanguage')}
        </button>
      </div>
    </div>
  );
};

export default SettingsPanel;
